import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { LogOut, ArrowLeftRight, RefreshCw, BarChart3, Users, ClipboardCheck } from "lucide-react";
import { syncQueue, getQueue, isOnline } from "@/lib/offline";
import { toast } from "sonner";

const MobileProfile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [queueCount, setQueueCount] = useState(getQueue().length);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const doSync = async () => {
    const r = await syncQueue();
    setQueueCount(getQueue().length);
    toast.success(`Synced ${r.synced} · ${r.remaining} pending`);
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  const roles = (user?.roles && user.roles.length ? user.roles : [user?.role]).filter(Boolean);

  return (
    <div className="px-4 pt-4 pb-6 space-y-4" data-testid="mobile-profile-page">
      {/* Identity card */}
      <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1B2C5A] to-[#14224A] text-white">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/10 border border-[#9DCD3F]/40 grid place-items-center text-[#9DCD3F] font-display text-lg">
            {user?.avatar_initials || (user?.name || "U").slice(0, 1)}
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg truncate">{user?.name}</div>
            <div className="text-xs text-white/70 truncate">{user?.email}</div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {roles.map((r) => (
            <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-[#9DCD3F]/20 text-[#9DCD3F] border border-[#9DCD3F]/30">{ROLE_LABELS[r] || r}</span>
          ))}
        </div>
      </div>

      {/* Mini stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-2">
          <MiniStat icon={Users} label="Students" value={stats.students_total} />
          <MiniStat icon={ClipboardCheck} label="Present" value={stats.present_today} accent />
          <MiniStat icon={BarChart3} label="Absent" value={stats.absent_today} />
        </div>
      )}

      {/* Sync card */}
      <div className="p-4 rounded-2xl bg-white border border-[#E2E8F0]">
        <div className="text-xs uppercase tracking-widest text-[#9DCD3F]">Offline Queue</div>
        <div className="flex items-center justify-between mt-1">
          <div className="font-display text-2xl text-[#1B2C5A]">{queueCount}</div>
          <Button size="sm" onClick={doSync} disabled={queueCount === 0 || !isOnline()} className="bg-[#1B2C5A] text-white">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync now
          </Button>
        </div>
        <p className="text-xs text-[#475569] mt-1">
          {queueCount === 0 ? "All synced." : isOnline() ? "Tap Sync to upload pending records." : "Will auto-sync when back online."}
        </p>
      </div>

      {/* Switch + Logout */}
      <Button onClick={() => navigate("/app")} variant="outline" className="w-full h-12 rounded-full border-[#E2E8F0]" data-testid="switch-to-desktop">
        <ArrowLeftRight className="w-4 h-4 mr-1.5" /> Switch to full dashboard
      </Button>
      <Button onClick={doLogout} variant="outline" className="w-full h-12 rounded-full border-red-200 text-red-700 hover:text-red-800" data-testid="mobile-logout">
        <LogOut className="w-4 h-4 mr-1.5" /> Sign out
      </Button>
      <div className="text-center text-[10px] text-[#475569] mt-2">DFA Teacher PWA · v1.0 · Knowledge & Action</div>
    </div>
  );
};

const MiniStat = ({ icon: Icon, label, value, accent }) => (
  <div className={"p-3 rounded-xl border " + (accent ? "border-l-4 border-l-[#9DCD3F] border-[#E2E8F0] bg-white" : "border-[#E2E8F0] bg-white")}>
    <div className="flex items-center justify-between">
      <Icon className="w-4 h-4 text-[#1B2C5A]" />
    </div>
    <div className="mt-2 font-display text-xl text-[#1B2C5A]">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-[#9DCD3F] mt-0.5">{label}</div>
  </div>
);

export default MobileProfile;
